import { listSpec, oneSpec, type Prefetch } from '@bemor/web-kit';
import { activitySpec } from '@bemor/web-kit/ui';

import { P } from '@/lib/permissions';

import { alertsPrefetch } from '@/pages/alerts/index';
import { approvalReads } from '@/pages/approvals/detail';
import { approvalsPrefetch } from '@/pages/approvals/index';
import { approvalRulesReads } from '@/pages/approvals/rules';
import { auditPrefetch } from '@/pages/audit/index';
import { caseReads } from '@/pages/cases/detail';
import { casesPrefetch } from '@/pages/cases/index';
import { chargeReads } from '@/pages/charges/detail';
import { chargesPrefetch } from '@/pages/charges/index';
import { disputeReads } from '@/pages/disputes/detail';
import { disputesPrefetch } from '@/pages/disputes/index';
import { flagReads } from '@/pages/flags/detail';
import { flagsPrefetch } from '@/pages/flags/index';
import { homeReads } from '@/pages/home';
import { httpLogsPrefetch } from '@/pages/http-logs/index';
import { merchantReads } from '@/pages/merchants/detail';
import { merchantsPrefetch } from '@/pages/merchants/index';
import { refundsPrefetch } from '@/pages/money/refunds';
import { taxPrefetch } from '@/pages/money/tax';
import { notificationLogsPrefetch } from '@/pages/notification-logs/index';
import { notificationsPrefetch } from '@/pages/notifications/index';
import { orderReads } from '@/pages/orders/detail';
import { ordersPrefetch } from '@/pages/orders/index';
import { paymentMethodReads } from '@/pages/payment-methods/detail';
import { paymentMethodsPrefetch } from '@/pages/payment-methods/index';
import { policyReads } from '@/pages/policies/detail';
import { policiesReads } from '@/pages/policies/index';
import { binImportsReads } from '@/pages/pricing/bin-imports';
import { cardReads } from '@/pages/pricing/card';
import { cardsPrefetch } from '@/pages/pricing/cards';
import { merchantPricingSpec } from '@/pages/pricing/merchant-pricing';
import { reservePoliciesPrefetch } from '@/pages/pricing/reserve-policies';
import { reserveSchedulePrefetch } from '@/pages/pricing/reserve-schedule';
import { queuePrefetch } from '@/pages/queues/index';
import { financeReads } from '@/pages/reports/finance';
import { metricsReads } from '@/pages/reports/metrics';
import { riskCaseReads } from '@/pages/risk/case';
import { riskDashboardReads } from '@/pages/risk/dashboard';
import { riskKeyReads } from '@/pages/risk/key';
import { riskMonitoringPrefetch } from '@/pages/risk/monitoring';
import { riskMonitoringReads } from '@/pages/risk/monitoring-detail';
import { riskReviewsPrefetch } from '@/pages/risk/reviews';
import { riskRulesPrefetch } from '@/pages/risk/rules';
import { searchReads } from '@/pages/search/index';
import { sessionReads } from '@/pages/sessions/detail';
import { sessionsPrefetch } from '@/pages/sessions/index';
import { preferencesReads } from '@/pages/settings/notifications';
import { pushReads } from '@/pages/settings/push';
import { templatesPrefetch } from '@/pages/settings/templates';
import { staffPrefetch } from '@/pages/staff/index';
import { rolesPrefetch } from '@/pages/staff/roles';
import { subscriptionReads } from '@/pages/subscriptions/detail';
import { subscriptionsPrefetch } from '@/pages/subscriptions/index';
import { tenantReads } from '@/pages/tenants/detail';
import { tenantsPrefetch } from '@/pages/tenants/index';

/** The shell's own read on every signed-in page: the queue counts on the nav (`AppLayout`). */
export const layoutPrefetch: Prefetch = ({ can }) =>
    can(P.queuesRead) ? [listSpec('/staff/queues')] : [];

/**
 * What each route prefetches on the server (BM-46 §2.0), from the same `<page>Reads` the page runs, so a server-
 * rendered page finds its data in the cache. Reads a user must type first (search under three characters) and
 * the comment threads (paged in the browser) are not prefetched.
 */
export const PREFETCH: Record<string, Prefetch> = {
    '/': ({ can }) => {
        const r = homeReads();
        return [
            ...(can(P.queuesRead) ? [r.queues] : []),
            ...(can(P.approvalsRead) ? [r.approvals] : []),
            ...(can(P.alertsRead) ? [r.alerts] : []),
            ...(can(P.reportsRead) ? [r.now] : []),
        ];
    },
    '/queues/:queue': queuePrefetch,
    '/cases': casesPrefetch,
    '/cases/:id': ({ params, can }) => [
        ...Object.values(caseReads(params.id)),
        // BM-46 §2.9: the record's activity (audit log), for staff who may read it
        ...(can(P.auditRead) && params.id
            ? [activitySpec('case', params.id)]
            : []),
    ],
    '/merchants': merchantsPrefetch,
    '/merchants/:id': ({ params, can }) => [
        ...Object.values(merchantReads(params.id)),
        // BM-62: the merchant's pricing, for staff who may read it
        ...(can(P.pricingRead) && params.id
            ? [merchantPricingSpec(params.id)]
            : []),
        // BM-46 §2.9: the record's activity (audit log), for staff who may read it
        ...(can(P.auditRead) && params.id
            ? [activitySpec('merchant', params.id)]
            : []),
    ],
    '/audit': auditPrefetch,
    '/search': ({ url }) => {
        const r = searchReads(url.searchParams);
        return r.list ? [r.list] : [];
    },
    '/flags': flagsPrefetch,
    '/flags/:key': ({ params }) => Object.values(flagReads(params.key)),
    '/approvals': approvalsPrefetch,
    '/approvals/rules': () => Object.values(approvalRulesReads()),
    '/approvals/:id': ({ params }) => Object.values(approvalReads(params.id)),
    '/alerts': alertsPrefetch,
    '/http-logs': httpLogsPrefetch,
    '/http-logs/:id': httpLogsPrefetch,
    '/notification-logs': notificationLogsPrefetch,
    '/notification-logs/:id': notificationLogsPrefetch,
    '/money/refunds': refundsPrefetch,
    '/money/charges': chargesPrefetch,
    '/money/charges/:id': ({ params }) => Object.values(chargeReads(params.id)),
    '/orders': ordersPrefetch,
    '/orders/:id': ({ params }) => Object.values(orderReads(params.id)),
    '/subscriptions': subscriptionsPrefetch,
    '/subscriptions/:id': ({ params }) =>
        Object.values(subscriptionReads(params.id)),
    '/payment-methods': paymentMethodsPrefetch,
    '/payment-methods/:id': ({ params }) =>
        Object.values(paymentMethodReads(params.id)),
    '/sessions': sessionsPrefetch,
    '/sessions/:id': ({ params }) => Object.values(sessionReads(params.id)),
    '/money/tax': taxPrefetch,
    // BM-122
    '/reports/finance': ({ url }) => Object.values(financeReads(url)),
    '/reports/metrics': ({ url }) => Object.values(metricsReads(url)),
    '/pricing/cards': cardsPrefetch,
    '/pricing/cards/:id': ({ params }) => Object.values(cardReads(params.id)),
    '/pricing/reserve-policies': reservePoliciesPrefetch,
    // BM-125 slice 2: the review queue, a case, a buyer key
    '/risk/reviews': riskReviewsPrefetch,
    '/risk/reviews/:id': ({ params }) =>
        Object.values(riskCaseReads(params.id)),
    '/risk/keys/:kind/:key': ({ params }) =>
        Object.values(riskKeyReads(params.kind, params.key)),
    // BM-125 slice 3: monitoring, a merchant's monitoring, the dashboard
    '/risk/monitoring': riskMonitoringPrefetch,
    '/risk/monitoring/:merchant': ({ params }) =>
        Object.values(riskMonitoringReads(params.merchant)),
    '/risk/dashboard': ({ url }) => Object.values(riskDashboardReads(url)),
    // BM-125: the rules page reads the registry, the lists and the profiles beside the list
    '/risk/rules': (ctx) => [
        ...riskRulesPrefetch(ctx),
        oneSpec('/staff/risk-rules/fields'),
        oneSpec('/staff/risk/lists'),
        oneSpec('/staff/risk/profiles'),
    ],
    '/pricing/reserve-schedule': reserveSchedulePrefetch,
    '/pricing/bin-imports': () => Object.values(binImportsReads()),
    '/disputes': disputesPrefetch,
    '/disputes/:id': ({ params }) => Object.values(disputeReads(params.id)),
    '/policies': () => Object.values(policiesReads()),
    '/policies/:kind': ({ params }) => Object.values(policyReads(params.kind)),
    '/staff': staffPrefetch,
    '/staff/roles': rolesPrefetch,
    '/tenants': tenantsPrefetch,
    '/tenants/:id': ({ params, can }) => [
        ...Object.values(tenantReads(params.id)),
        // BM-46 §2.9: the record's activity (audit log), for staff who may read it
        ...(can(P.auditRead) && params.id
            ? [activitySpec('tenant', params.id)]
            : []),
    ],
    '/notifications': notificationsPrefetch,
    '/settings/notifications': () => Object.values(preferencesReads()),
    '/settings/templates': templatesPrefetch,
    '/settings/push': () => Object.values(pushReads()),
};
